<template>
  <div class="card">
    <div class="card-body table-responsive position-relative">
      <b-table
        striped
        bordered
        foot-clone
        show-empty
        responsive="sm"
        :fields="fields"
        :busy="isLoading"
        class="datatables"
        :items="tableItems"
        :tbody-tr-class="rowClass"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <template #cell(NM_PRODUTOR_RURAL)="data">
          {{ formatNome(data.value) }}
        </template>

        <template v-if="!importacao" v-slot:cell(ST_PROJETO)="data">
          <span
            class="badge badge-pill font-size-12"
            :class="{
              'badge-soft-info': data.value === 'ELABORADO',
              'badge-soft-primary': data.value === 'INCOMPLETO',
              'badge-soft-success': data.value === 'LIBERADO',
              'badge-soft-dark': data.value === 'LIQUIDADO',
              'badge-soft-danger': data.value === 'EXCLUIDO',
              'badge-soft-warning': data.value === 'BAIXA PARCIAL'
            }"
          >
            {{ data.value }}
          </span>
        </template>

        <template v-else v-slot:cell(ST_IMPORTACAO)="data">
          <span
            class="badge badge-pill font-size-12"
            :class="{
              'badge-soft-info': data.value === 'COMPLETO',
              'badge-soft-success': data.value === 'EXPORTADO',
              'badge-soft-danger': data.value === 'INCOMPLETO'
            }"
          >
            {{ data.value }}
          </span>
        </template>

        <template #cell(interacao)="data">
          <div
            v-if="
              (data.item.ST_PROJETO !== 'ELABORADO' && !importacao) ||
                (importacao && data.item.ST_IMPORTACAO === 'EXPORTADO')
            "
          >
            <b-button
              size="sm"
              variant="primary"
              class="me-1"
              :disabled="loadingAcoes || isLoading"
              data-tooltip="Visualizar"
              data-tooltip-location="left"
              @click="emitirEditarItem(data.item)"
            >
              <i class="mdi mdi-eye"></i>
            </b-button>
          </div>
          <div class="d-flex gap-2" v-else>
            <b-button
              size="sm"
              variant="primary"
              data-tooltip="Editar"
              :disabled="loadingAcoes || isLoading"
              data-tooltip-location="left"
              @click="emitirEditarItem(data.item)"
            >
              <i class="mdi mdi-pencil"></i>
            </b-button>
            <b-button
              size="sm"
              v-if="!importacao"
              variant="danger"
              data-tooltip="Excluir"
              :disabled="loadingAcoes || isLoading"
              data-tooltip-location="left"
              @click="deletarProjeto(data.item)"
              class="d-flex align-items-center justify-content-center"
            >
              <i v-if="!loadingAcoes" class="mdi mdi-trash-can"></i>
              <b-spinner small variant="white" v-if="loadingAcoes"></b-spinner>
            </b-button>
          </div>
        </template>

        <!-- Templates do footer -->
        <template #foot()="">
          <h6></h6>
        </template>
        <template #foot(NM_PRODUTOR_RURAL)="">
          <div class="text-end">
            <span class="text-dark fw-bold">TOTAIS:</span>
          </div>
        </template>
        <template #foot(VL_ORCADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlOrcado)
            }}</span>
          </div>
        </template>
        <template #foot(VL_ASTEC)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlAstec)
            }}</span>
          </div>
        </template>
        <template #foot(VL_LIBERADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlLiberado)
            }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_FOLHA)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlSaldoFolha)
            }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_RDV)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlSaldoRdv)
            }}</span>
          </div>
        </template>
        <template #empty>
          <div class="text-center">
            <span class="text-muted">{{ defaultMensagem }}</span>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-end mt-3">
        <b-pagination
          :total-rows="total"
          :per-page="pageSize"
          v-model="currentPage"
          :disabled="isLoading"
          @change="onPageChange"
          class="pagination pagination-rounded mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import clienteRuralMixin from "@/tools/mixins/clienteRuralMixin.js";
import { paginacaoProjetosHelper } from "@/store/helpers/vuex/astec.js";

export default {
  mixins: [clienteRuralMixin],
  name: "Grid",

  props: {
    filtro: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Array,
      default: () => []
    },
    importacao: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    defaultMensagem: {
      type: String,
      default: "Nenhum registro disponível."
    }
  },
  data() {
    return {
      fields: [
        { key: "NR_PROJETO", label: "N° Projeto", sortable: true },
        {
          key: "NM_INSTITUICAO_FINANCEIRA",
          label: "Instituição Financeira",
          sortable: true
        },
        { key: "NM_LINHA_CREDITO", label: "Linha Crédito", sortable: true },
        { key: "CD_SAFRA", label: "Safra", sortable: true },
        { key: "DS_TP_PROJETO", label: "Tipo Projeto", sortable: true },
        {
          key: "DS_ITEM_AGROPECUARIO",
          label: "Item Agropecuário",
          sortable: true
        },
        {
          key: "NM_PRODUTOR_RURAL",
          label: "Cliente Rural",
          sortable: true
        },
        {
          key: "VL_ORCADO",
          label: "Valor Orçado",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_ASTEC",
          label: "Valor Astec",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_LIBERADO",
          label: "Valor Liberado",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_SALDO_FOLHA",
          label: "Saldo FOL",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_SALDO_RDV",
          label: "Saldo RDV",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        { key: "ST_PROJETO", label: "Status", sortable: true },
        { key: "interacao", label: "Ações" }
      ],
      loadingAcoes: false
    };
  },

  computed: {
    ...paginacaoProjetosHelper({
      state: ["currentPage", "pageSize", "total", "loading"],
      getters: [
        "currentPage",
        "pageSize",
        "total",
        "loading",
        "apiParams",
        "totalPages",
        "hasNextPage",
        "hasPrevPage",
        "rangeStart",
        "rangeEnd"
      ],
      actions: []
    }).computed,
    totalVlOrcado() {
      return parseFloat(this.items?.totais?.VL_ORCADO_TOTAL || 0);
    },
    totalVlAstec() {
      return parseFloat(this.items?.totais?.VL_ASTEC_TOTAL || 0);
    },
    totalVlLiberado() {
      return parseFloat(this.items?.totais?.VL_LIBERADO_TOTAL || 0);
    },
    totalVlSaldoFolha() {
      return parseFloat(this.items?.totais?.VL_SALDO_FOLHA_TOTAL || 0);
    },
    totalVlSaldoRdv() {
      return parseFloat(this.items?.totais?.VL_SALDO_RDV_TOTAL || 0);
    },
    noItems() {
      if (this.isLoading) return false;
      if (!this.items) return true;
      if (Array.isArray(this.items) && this.items.length === 0) return true;
      if (this.items.data && this.items.data.length === 0) return true;
      return false;
    },
    tableItems() {
      if (this.items && Array.isArray(this.items.data)) {
        return this.items.data;
      }
      return [];
    }
  },

  watch: {
    items: {
      deep: true,
      handler(newVal) {
        if (
          newVal &&
          newVal.current_page &&
          newVal.current_page !== this.currentPage
        ) {
          this.setPage(newVal.current_page);
        }
        if (
          newVal &&
          newVal.total !== undefined &&
          newVal.total !== this.total
        ) {
          this.setTotal(newVal.total);
        }
        if (newVal && newVal.per_page && newVal.per_page !== this.pageSize) {
          this.setPageSize(newVal.per_page);
        }
      },
      immediate: true
    }
  },

  created() {
    if (this.importacao && !this.fields.find(f => f.key === "ST_IMPORTACAO")) {
      this.fields.splice(12, 1, {
        key: "ST_IMPORTACAO",
        label: "Status Importação",
        sortable: true
      });
    }
    if (
      this.items.current_page &&
      this.items.current_page !== this.currentPage
    ) {
      this.setPage(this.items.current_page);
    }
    if (this.items.total !== undefined && this.items.total !== this.total) {
      this.setTotal(this.items.total);
    }
    if (this.items.per_page && this.items.per_page !== this.pageSize) {
      this.setPageSize(this.items.per_page);
    }
  },

  methods: {
    ...paginacaoProjetosHelper({
      state: [],
      getters: [],
      actions: [
        "setPage",
        "setPageSize",
        "setTotal",
        "setLoading",
        "syncWithApi"
      ]
    }).methods,
    rowClass(item, type) {
      if (!item || type !== "row") return "";
      if (item.ST_PROJETO === "EXCLUIDO") {
        return "table-danger";
      }
      return "";
    },

    calcularTotal(campo) {
      if (!this.items.data || this.items.data.length === 0) return 0;

      return this.items.data.reduce((total, item) => {
        return total + parseFloat(item[campo] || 0);
      }, 0);
    },

    emitirEditarItem(data) {
      this.$store.commit("PARAMETROS_PROJETO_ASTEC", data);
      localStorage.setItem(
        "exibirCamposImportacao",
        this.importacao.toString()
      );
      this.$router.push("/astec/projetos/novoProjeto");
    },

    deletarProjeto(projeto) {
      Swal.fire({
        title: "Tem certeza?",
        text:
          "Você realmente deseja excluir este projeto? Essa ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          this.loadingAcoes = true;

          const obj = {
            sqUsuario: this.$store.state.user,
            cdCooperado: projeto.CD_COOPERADO,
            sqProjetoVar: projeto.SQ_PROJETO_VAR
          };

          this.$http
            .post("/api/astec/deletarProjeto", obj)
            .then(() => {
              Swal.fire({
                icon: "success",
                title: "Sucesso",
                text: "Projeto excluído com sucesso!"
              });

              this.$emit("projetoAstecFiltrados", {
                ...this.filtro,
                page: this.currentPage,
                per_page: this.pageSize
              });
            })
            .catch(err => {
              this.$tools.alerta(
                err.response?.data?.message || "Erro ao excluir projeto",
                "error"
              );
            })
            .finally(() => {
              this.loadingAcoes = false;
            });
        }
      });
    },

    onPageChange(page) {
      this.setPage(page);
      this.$emit("projetoAstecFiltrados", {
        ...this.filtro,
        page: this.currentPage,
        per_page: this.pageSize
      });
    },

    formatNome(nome) {
      if (!nome) return "";
      return nome
        .toLowerCase()
        .split(" ")
        .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1))
        .join(" ");
    }
  }
};
</script>

<style scoped>
.sortable-header {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.sort-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 5px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
